import { DataTable, FlexBox, IconButton, Page } from '@dixa/lib-shared-habla';
import { client as elevioClient } from 'elevio';

import { facebookMessengerApi, FacebookPage } from 'apis/facebookMessenger';
import { appActions } from 'app/app.actions';
import { MessagingQueueDetails } from 'app/views/layout/views/settings/views/messaging/components/queueDetails/messagingQueueDetails';
import { NotificationType, useNotifications } from 'hooks/useNotifications';
import { useAppDispatch } from 'utils/redux/useAppDispatch';

import { FacebookAuthenticator } from './components/facebookAuthenticator/facebookAuthenticator';
import { FacebookEditPageModal } from './components/facebookEditPageModal/facebookEditPageModal';

/** Represents the Facebook section. */
export const Facebook = (): JSX.Element | null => {
  const dispatch = useAppDispatch();
  const notify = useNotifications();

  const [deleteFacebookPage] =
    facebookMessengerApi.useDeleteFacebookPageMutation();

  const { facebookApp, isFetching, refetch } =
    facebookMessengerApi.useReadApplicationsQuery(undefined, {
      refetchOnMountOrArgChange: true,
      selectFromResult: ({ data, ...rest }) => ({
        ...rest,
        facebookApp: data?.data.applications?.find(
          (app) => app.type === 'common',
        ),
      }),
    });

  const handleEdit = (facebookPage: FacebookPage) => {
    dispatch(
      appActions.showModal({
        modal: {
          type: 'settings:facebookEditPage',
          arg: { facebookPage },
        },
      }),
    );
  };

  const handleRemove = async (facebookPage: FacebookPage) => {
    const { isConfirmed, confirm } = await dispatch(
      appActions.showModal({
        modal: {
          type: 'app:confirm',
          arg: {
            title: `Remove "${facebookPage.name}"`,
            description:
              'Are you sure you want to remove your Facebook page from Dixa?',
            confirm: {
              label: 'Remove',
              async: true,
              asyncLabel: 'Removing...',
            },
          },
        },
      }),
    );

    if (isConfirmed) {
      try {
        confirm(async () => {
          await deleteFacebookPage({
            pageId: facebookPage.id,
          }).unwrap();

          notify(
            {
              title: 'Facebook page successfully removed.',
              time: 3000,
            },
            NotificationType.Success,
          );
        });
      } catch {
        notify(
          {
            title: 'An error occurred while removing the Facebook page.',
          },
          NotificationType.Error,
        );
      }
    }
  };

  if (!facebookApp) return null;

  return (
    <>
      <Page.Section
        title="Facebook"
        controls={
          <FacebookAuthenticator
            appId={facebookApp?.id}
            onSuccess={refetch}
            facebookButtonProps={{
              variant: 'secondary',
              platform: 'facebook',
              icon: 'plus',
            }}
          >
            Facebook page
          </FacebookAuthenticator>
        }
      >
        <DataTable
          data={facebookApp.pages}
          columns={[
            {
              label: 'ID',
              selector: (page) => page.id,
              style: {
                width: '30%',
              },
            },
            {
              label: 'Name',
              selector: (page) => page.name,
              style: {
                width: '30%',
              },
            },
            {
              label: 'Queue',
              selector: (page) => (
                <MessagingQueueDetails queueId={page.queue_id} />
              ),
              style: {
                width: '30%',
              },
            },
            {
              label: '',
              selector: (page) => (
                <FlexBox justifyContent="flex-end">
                  <IconButton
                    variant="tertiary"
                    icon="pen"
                    onClick={() => handleEdit(page)}
                  />

                  <IconButton
                    variant="tertiary"
                    icon="trash"
                    onClick={() => handleRemove(page)}
                  />
                </FlexBox>
              ),
              style: {
                width: 0,
              },
            },
          ]}
          tableStatusProps={{
            loading: isFetching,
            ...(!isFetching &&
              !facebookApp.pages?.length && {
                emptyState: {
                  icon: 'facebook-messenger',
                  title: 'Get started with Facebook',
                  description:
                    'Click the "+ Facebook page" button above and we\'ll help you through the setup process of the Facebook channel.',
                  buttonProps: {
                    children: 'Learn more',
                    variant: 'secondary',
                    onClick: () => elevioClient.openArticle('1023'),
                  },
                },
              }),
          }}
        />
      </Page.Section>

      <FacebookEditPageModal />
    </>
  );
};
